<template>
  <div class="container" :class="innerDisabled? 'disabled' : ''">
    <input
      class="file-input"
      ref="fileInput"
      type="file"
      accept="image/*"
      :disabled="innerDisabled"
      @change="hanldeFileChange"
      @click="$emit('input-click', $event)"
    />

    <!-- loading -->
    <template v-if="loading">
      <slot name="loading">
        <div class="img-loading">上传中...</div>
      </slot>
    </template>

    <!-- preview -->
    <template v-else-if="previewSrc">
      <img class="preview" :src="previewSrc" />
    </template>

    <!-- button -->
    <template v-else>
      <slot name="input">
        <div class="plus-icon" />
      </slot>
    </template>
  </div>
</template>

<script>
import { Toast } from "vant";
// import { uploadFile } from "@wifikey/upload-image";
import { uploadFile } from "../../../index";
import { getQiniuUploadImageToken } from "../../services/common";
import { getToken } from "../../services/index";
import { compressor } from "../../utils/image";

// 图片回显缓存
const imageCacheMap = {};

export default {
  name: "wk-single-image-upload",
  model: {
    prop: "value",
    event: "change",
  },

  watch: {
    value() {
      // 如果命中回显缓存，直接从缓存中回显图片
      this.setPreviewSrcFromCache();
    },
  },

  created() {
    // 如果命中回显缓存，直接从缓存中回显图片
    this.setPreviewSrcFromCache();
  },

  props: {
    value: {
      type: String,
      default: "",
    },

    dest: {
      type: String,
      default: "qiniu",
      validator: (value) => ["qiniu", "wifikey"].includes(value),
    },

    // 单位: MB
    limit: {
      type: Number,
      default: 20,
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      previewSrc: '',
    };
  },
  computed: {
    innerDisabled() {
      return this.disabled || this.loading;
    },

    limitByte() {
      return this.limit * 1024 * 1024;
    },

  },

  methods: {
    async hanldeFileChange(evt) {
      // eslint-disable-next-line prefer-destructuring
      const files = evt.target.files;
      const [file] = evt.target.files;

      if (!files.length) {
        return;
      }

      const errorMessage = this.validate(file);
      if (errorMessage) {
        Toast.fail(errorMessage);
        this.$refs.fileInput.value = "";
        return;
      }

      let ossKey;

      // 在确认要进行上传时，清空现有 previewSrc，避免上传后闪烁
      this.previewSrc = '';

      await compressor(file, {
        quality: 0.8,
        minWidth: 200,
        minHeight: 200,
        convertSize: 5000000,
      }).then(async (res) => {
        console.log(`=====`, res);
        try {
          this.loading = true;

          if (this.dest === "qiniu") {
            ossKey = await this.uploadFileToQiniu(res.file);
          }

          if (this.dest === "wifikey") {
            ossKey = await this.uploadFileToWifikey(res.file);
          }

          const uploadedSrc = this.getUploadedSrc(ossKey);

          this.$emit('change', uploadedSrc);

          imageCacheMap[uploadedSrc] = res.content;
          this.previewSrc = res.content;

        } catch (error) {
          this.$emit('change', '');
          Toast.fail("上传图片失败，请稍候重试");
          console.log(`error`, error);
          return;
        } finally {
          this.loading = false;
          // 清除 input:file 中的 value 数据
          // 避免在连续使用拍摄照片时不触发 change 事件问题
          this.$refs.fileInput.value = '';
        }

      });
    },

    getUploadedSrc(ossKey) {
      const HOST_MAP = {
        qiniu: process.env.QINIU_IMG_URL,
        wifikey: "",
      };
      return ossKey ? `${HOST_MAP[this.dest]}${ossKey}` : "";
    },

    validate(file) {
      if (file.size > this.limitByte) {
        return `图片大小不能超过 ${this.limit}MB`;
      }

      return false;
    },

    // 上传七牛
    async uploadFileToQiniu(file) {
      const {
        data: { uploadToken },
      } = await getQiniuUploadImageToken({});
      const requestParams = {
        file,
        token: uploadToken,
      };
      const imageList = await uploadFile(requestParams);
      const img = imageList[0] || {};
      const { key } = img && img.data;
      return key;
    },

    // 上传私有服务器
    async uploadFileToWifikey(file) {
      // pass
      const bizId = "M_PRODUCT";
      const {
        data: { uploadToken },
      } = await getQiniuUploadImageToken({ bizId });
      const requestParams = { file, token: uploadToken, bizId: "wk_0012" };
      const imgList = await uploadFile(requestParams, "wifikey");
      const img = imgList[0] && imgList[0].data;
      return img.url;
    },

    setPreviewSrcFromCache() {
      const imageCache = imageCacheMap[this.value];
      const isCacheHitted = Boolean(imageCache);
      this.previewSrc = isCacheHitted ? imageCache : this.hanldeImageUrl();
      return isCacheHitted;
    },

    hanldeImageUrl() {
      const HOST_MAP = {
        qiniu: "",
        wifikey: process.env.WK_IMG_URL,
      };
      return this.value ? `${HOST_MAP[this.dest]}${this.value}` : "";
    },

  },
};
</script>

<style scoped>
.container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border: 1px solid #ebebeb;
  border-radius: 4px;
  background-color: #f4f5f5;
  overflow: hidden;
}
.container:not(.disabled):active {
  filter: brightness(0.98);
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
}

.img-loading {
  color: #999;
  font-size: 13px;
}

.plus-icon {
  width: 22px;
  height: 22px;
  background-color: #f00;
  background: url("./plus.png") center / contain no-repeat;
}

.preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>



// WEBPACK FOOTER //
// index.vue?041cae77